/**
 * Custom React Hook for Assessments API
 */

import { useState, useEffect, useCallback } from 'react';
import assessmentService from '../services/assessmentService';
import {
  AssessmentDTO,
  CreateAssessmentRequest,
  AssessmentType,
  AssessmentStatus,
  PageResponse,
} from '../types/assessment.types';

interface UseAssessmentsProps {
  residentId?: string;
  autoFetch?: boolean;
}

export const useAssessments = ({ residentId, autoFetch = true }: UseAssessmentsProps = {}) => {
  const [assessments, setAssessments] = useState<AssessmentDTO[]>([]);
  const [currentAssessment, setCurrentAssessment] = useState<AssessmentDTO | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const pageSize = 20;

  // Fetch all assessments with pagination
  const fetchAssessments = useCallback(async (page: number = 0) => {
    setLoading(true);
    setError(null);
    try {
      const response: PageResponse<AssessmentDTO> = await assessmentService.getAllAssessments(page, pageSize);
      setAssessments(response.content);
      setTotalElements(response.totalElements);
      setTotalPages(response.totalPages);
      setCurrentPage(response.number);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch assessments');
      console.error('Error fetching assessments:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch assessments by resident
  const fetchAssessmentsByResident = useCallback(async (resId: string) => {
    setLoading(true);
    setError(null);
    try {
      const assessmentList = await assessmentService.getAssessmentsByResident(resId);
      setAssessments(assessmentList);
      setTotalElements(assessmentList.length);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch assessments for resident');
      console.error('Error fetching assessments by resident:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch assessments by type
  const fetchAssessmentsByType = useCallback(async (assessmentType: AssessmentType) => {
    setLoading(true);
    setError(null);
    try {
      const assessmentList = await assessmentService.getAssessmentsByType(assessmentType);
      setAssessments(assessmentList);
      setTotalElements(assessmentList.length);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch assessments by type');
      console.error('Error fetching assessments by type:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch assessments by status
  const fetchAssessmentsByStatus = useCallback(async (status: AssessmentStatus) => {
    setLoading(true);
    setError(null);
    try {
      const assessmentList = await assessmentService.getAssessmentsByStatus(status);
      setAssessments(assessmentList);
      setTotalElements(assessmentList.length);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch assessments by status');
      console.error('Error fetching assessments by status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch assessment by ID
  const fetchAssessmentById = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const assessment = await assessmentService.getAssessmentById(id);
      setCurrentAssessment(assessment);
      return assessment;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch assessment');
      console.error('Error fetching assessment:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new assessment
  const createAssessment = useCallback(async (request: CreateAssessmentRequest) => {
    setSaving(true);
    setError(null);
    try {
      const newAssessment = await assessmentService.createAssessment(request);
      setAssessments((prev) => [newAssessment, ...prev]);
      setCurrentAssessment(newAssessment);
      return newAssessment;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create assessment');
      console.error('Error creating assessment:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  // Update assessment
  const updateAssessment = useCallback(async (id: string, data: Partial<AssessmentDTO>) => {
    setSaving(true);
    setError(null);
    try {
      const updatedAssessment = await assessmentService.updateAssessment(id, data);
      setAssessments((prev) =>
        prev.map((assessment) => (assessment.id === id ? updatedAssessment : assessment))
      );
      if (currentAssessment?.id === id) {
        setCurrentAssessment(updatedAssessment);
      }
      return updatedAssessment;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update assessment');
      console.error('Error updating assessment:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [currentAssessment]);

  // Submit assessment
  const submitAssessment = useCallback(async (id: string) => {
    setSaving(true);
    setError(null);
    try {
      const submittedAssessment = await assessmentService.submitAssessment(id);
      setAssessments((prev) =>
        prev.map((assessment) => (assessment.id === id ? submittedAssessment : assessment))
      );
      if (currentAssessment?.id === id) {
        setCurrentAssessment(submittedAssessment);
      }
      return submittedAssessment;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit assessment');
      console.error('Error submitting assessment:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [currentAssessment]);

  // Approve assessment
  const approveAssessment = useCallback(async (id: string) => {
    setSaving(true);
    setError(null);
    try {
      const approvedAssessment = await assessmentService.approveAssessment(id);
      setAssessments((prev) =>
        prev.map((assessment) => (assessment.id === id ? approvedAssessment : assessment))
      );
      if (currentAssessment?.id === id) {
        setCurrentAssessment(approvedAssessment);
      }
      return approvedAssessment;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to approve assessment');
      console.error('Error approving assessment:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [currentAssessment]);

  // Delete assessment
  const deleteAssessment = useCallback(async (id: string) => {
    setSaving(true);
    setError(null);
    try {
      await assessmentService.deleteAssessment(id);
      setAssessments((prev) => prev.filter((assessment) => assessment.id !== id));
      if (currentAssessment?.id === id) {
        setCurrentAssessment(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete assessment');
      console.error('Error deleting assessment:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [currentAssessment]);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      if (residentId) {
        fetchAssessmentsByResident(residentId);
      } else {
        fetchAssessments(0);
      }
    }
  }, [residentId, autoFetch, fetchAssessments, fetchAssessmentsByResident]);

  // Pagination handlers
  const handlePageChange = useCallback((page: number) => {
    fetchAssessments(page);
  }, [fetchAssessments]);

  const nextPage = useCallback(() => {
    if (currentPage < totalPages - 1) {
      fetchAssessments(currentPage + 1);
    }
  }, [currentPage, totalPages, fetchAssessments]);

  const prevPage = useCallback(() => {
    if (currentPage > 0) {
      fetchAssessments(currentPage - 1);
    }
  }, [currentPage, fetchAssessments]);

  return {
    // State
    assessments,
    currentAssessment,
    loading,
    saving,
    error,
    totalElements,
    totalPages,
    currentPage,
    pageSize,

    // Actions
    fetchAssessments,
    fetchAssessmentsByResident,
    fetchAssessmentsByType,
    fetchAssessmentsByStatus,
    fetchAssessmentById,
    createAssessment,
    updateAssessment,
    submitAssessment,
    approveAssessment,
    deleteAssessment,
    handlePageChange,
    nextPage,
    prevPage,
    setCurrentAssessment,
    setError,
  };
};
